import { useState, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import Icon from '../Icon';

type DropdownItemProps = {
  id: string;
  label: string;
  value: string | number;
  selected?: boolean;
};

interface Props {
  data: DropdownItemProps[];
  shadow?: boolean;
  width?: number;
  bordered?: boolean;
  prefix?: string;
  prefixClass?: string;
  postfix?: string;
  postfixClass?: string;
  className?: string;
}

function Dropdown({
  data,
  shadow = true,
  width,
  bordered = true,
  prefix,
  prefixClass,
  postfix,
  postfixClass,
  className
}: Props) {
  // https://headlessui.com/react/listbox
  // logic is incomplete and used for UI presentation
  const selectedIndex = data.findIndex(
    (item) => item.selected === true
  );

  const [selectedValue, setSelectedValue] = useState(
    selectedIndex >= 0 ? data[selectedIndex] : data[0]
  );

  return (
    <div
      style={{
        width: width ? `${width}px` : '100%'
      }}
      className={classNames([
        className,
        'relative flex h-[52px] items-center rounded-xl bg-white font-circular  text-sm font-normal !leading-none text-sl-blue sm:text-base',
        shadow && 'shadow-dropdown-depth-1',
        bordered &&
          'border border-sl-blue border-opacity-10'
      ])}
    >
      <Listbox
        value={selectedValue}
        onChange={setSelectedValue}
      >
        <Listbox.Button className="w-full px-4 text-left">
          <span
            className={classNames([
              'block truncate whitespace-nowrap text-sl-blue',
              bordered && 'mr-4',
              !bordered && 'mr-6 text-base sm:text-lg'
            ])}
          >
            {prefix && (
              <span className={`${prefixClass || ''}`}>
                {prefix}
              </span>
            )}
            <span
              className={classNames([
                'font-circular',
                bordered && 'font-bold text-sl-indigo',
                !bordered && 'font-medium text-sl-blue'
              ])}
            >
              {selectedValue.label}
            </span>
            {postfix && (
              <span className={`${postfixClass || ''}`}>
                {postfix}
              </span>
            )}
          </span>
          {bordered && (
            <Icon
              className="absolute top-1/2 right-3 -mt-2 text-base text-sl-indigo"
              icon="caret-down"
            />
          )}
          {!bordered && (
            <Icon
              className="absolute top-1/2 right-3 -mt-2 text-base text-sl-blue"
              icon="chevron-down"
            />
          )}
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          enter="transition ease-in duration-100"
          enterFrom="opacity-0 -transform-y-3"
          enterTo="opacity-100 transform-y-0"
          leaveFrom="opacity-100 transform-y-0"
          leaveTo="opacity-0 -transform-y-3"
        >
          <Listbox.Options className="absolute top-full left-0 right-0 z-50 mt-1 w-full overflow-hidden rounded-md bg-white text-left shadow-md">
            <div className="max-h-60 overflow-auto text-sm">
              {data.map((item) => (
                <Listbox.Option
                  key={item.id}
                  value={item}
                  as="ul"
                >
                  {({ active, selected }) => (
                    <li
                      className={`block cursor-pointer truncate whitespace-nowrap py-1.5 px-3 font-circular font-normal text-sl-blue transition-colors ${
                        active || selected
                          ? 'bg-sl-indigo bg-opacity-10 text-opacity-100'
                          : 'text-opacity-75'
                      }`}
                    >
                      {item.label}
                    </li>
                  )}
                </Listbox.Option>
              ))}
            </div>
          </Listbox.Options>
        </Transition>
      </Listbox>
    </div>
  );
}

export default Dropdown;
